import { useState, type FC, type FormEvent } from 'react';
import { User, Mail, Phone, Calendar, Shield, Pencil, X, Check, AlertCircle } from 'lucide-react';
import { useUserProfile, useUpdateProfile } from '../../../hooks/useUserProfile';
import { cn } from '../../../utils/cn';
import { useToast } from '../../../components/ui/Toast';

interface FieldProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  isEditing?: boolean;
  editValue?: string;
  onEditValueChange?: (value: string) => void;
  placeholder?: string;
  type?: string;
}

const ProfileField: FC<FieldProps> = ({
  label, value, icon, isEditing, editValue, onEditValueChange, placeholder, type = 'text',
}) => (
  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
    <div className="p-2 bg-white rounded-lg shadow-sm">{icon}</div>
    <div className="flex-1">
      <p className="text-sm text-gray-500">{label}</p>
      {isEditing && onEditValueChange ? (
        <input
          type={type}
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      ) : (
        <p className="text-gray-900 font-medium">{value || 'Not set'}</p>
      )}
    </div>
  </div>
);

export const ProfileSection: FC = () => {
  const { data: profile, isLoading, error: fetchError } = useUserProfile();
  const updateProfile = useUpdateProfile();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    if (profile) {
      setEditName(profile.name);
      setEditPhone(profile.phone || '');
      setError(null);
    }
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (editName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    try {
      await updateProfile.mutateAsync({
        name: editName.trim(),
        phone: editPhone.trim() || undefined,
      });
      showToast({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully.',
      });
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse w-32" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (fetchError || !profile) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <p className="text-red-800">Failed to load profile. Please try again.</p>
      </div>
    );
  }

  const memberSince = new Date(profile.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Profile</h2>
          <p className="text-sm text-gray-600 mt-1">Manage your account information</p>
        </div>
        {!isEditing ? (
          <button
            onClick={startEditing}
            className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Edit Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEditing}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={updateProfile.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <ProfileField
          label="Full Name"
          value={profile.name}
          icon={<User className="w-5 h-5 text-gray-600" />}
          isEditing={isEditing}
          editValue={editName}
          onEditValueChange={setEditName}
          placeholder="Enter your name"
        />
        <ProfileField
          label="Email Address"
          value={profile.email}
          icon={<Mail className="w-5 h-5 text-gray-600" />}
        />
        <ProfileField
          label="Phone Number"
          value={profile.phone || 'Not set'}
          icon={<Phone className="w-5 h-5 text-gray-600" />}
          isEditing={isEditing}
          editValue={editPhone}
          onEditValueChange={setEditPhone}
          placeholder="+1 (555) 123-4567"
          type="tel"
        />
        <ProfileField
          label="Member Since"
          value={memberSince}
          icon={<Calendar className="w-5 h-5 text-gray-600" />}
        />
        <ProfileField
          label="Email Verified"
          value={profile.emailVerified ? 'Yes' : 'No - Check your email'}
          icon={
            <Shield
              className={cn('w-5 h-5', profile.emailVerified ? 'text-green-600' : 'text-yellow-600')}
            />
          }
        />
      </div>
    </div>
  );
};

export default ProfileSection;
